//--------------------------------------------
//
// Arm scene - overlayed on top of DOM layer
//
//--------------------------------------------
'use strict';


import THREE from 'three';
import CONSTANTS from './constants';
import TextExplode from './TextExplode';
import AudioManager from './AudioManager';
import Scene from './Scene';
import Arm from './Arm';
import Oculus from './Oculus';

class ArmScene {

	constructor(){
		//DOM container
		this.container = document.getElementById('header');

		//Main THREE renderer
		this.renderer = null;

		//Post processing
		this.composer = null;

		//Scenes
		this.scene = null;

		//Cameras
		this.camera = null;

		//Sizing + Mouse pos
		this.w = window.innerWidth;
		this.h = window.innerHeight * CONSTANTS.HEIGHT_RATIO;
		this.windowHalfX = this.w / 2;
		this.windowHalfY = this.h / 2;
		this.targetRotationY = 0;
		this.targetRotationX = 0;
		this.mouseX = 0;
		this.mouseY = 0;
		this.x = 0;
		this.y = 0;
		this.mouse = {
			x: 0,
			y: 0
		};

		this.raycaster = null;

		//hand mesh
		this.hand = null;

		this.animating = true;


		//start
		this.init();
	}

	init(){
		this.addScene();
		this.addLights();
		this.addArm();

		//on mousemove, control arm
		window.addEventListener('mousemove', this.onMouse.bind(this), false);

		//resize
		window.addEventListener('resize', this.onResize.bind(this), false);

		this.animate();
	}

	//--------------------------------------------
	// Main scene setup
	//
	addScene(){
		//main scene
		this.scene = new THREE.Scene();

		this.addCameras();
		
		this.renderer = new THREE.WebGLRenderer({alpha: true});
		this.renderer.setPixelRatio(1);
		
		this.renderer.setSize(window.innerWidth, window.innerHeight * CONSTANTS.HEIGHT_RATIO);
		
		this.renderer.autoClear = false;

		this.renderer.domElement.classList.add('arm-canvas');
		
		this.container.appendChild(this.renderer.domElement);


	}

	//--------------------------------------------
	// Camera setup
	//	
	addCameras(){
		//Main scene camera
		this.camera = new THREE.PerspectiveCamera(45, this.w / this.h, 1, 10000);
		this.camera.position.z = -200;
		this.camera.lookAt(this.scene.position);
	}

	//--------------------------------------------
	// Add lights (for mesh phong)
	//		
	addLights(){
		const ambientLight = new THREE.AmbientLight( 0xffffff );
		this.scene.add(ambientLight);

		const pointLight = new THREE.PointLight( 0xffffff, 0.5 );
		pointLight.position.set( 0, 0, 0 );
		this.scene.add( pointLight );
	}

	//--------------------------------------------
	// Load arms async
	//
	addArm(){
		const self = this;

		let LeftArm = new Arm({
			geometrySrc: 'data/arm.js',
			textureSrc: 'data/FH_1.jpg',
			bumpSrc: 'data/FH_4.jpg',
			side: 'left'
		
		}).then((hand) => {
			self.hand = hand;
			self.scene.add(self.hand);

			//--------------------------------------------
			// Display welcome text on arm load
			//
			AudioManager.onStartup();
					
		});
	}

			

	//--------------------------------------------
	//
	// Event Handlers
	//
	//--------------------------------------------
	onMouse(ev){

		this.mouse.x = (ev.clientX / window.innerWidth) * 2 - 1;
		this.mouse.y = - (ev.clientY / window.innerHeight) * 2 + 1;

		// if (ev.clientX > this.windowHalfX){
		// 	this.mouse.x += this.mouse.x;
		// }

		if (ev.clientY < this.windowHalfY){
			this.mouse.y += this.mouse.y;
		}
		

		const vector = new THREE.Vector3(this.mouse.x, this.mouse.y - 0.25, 0);
		vector.unproject(this.camera);

		const dir = vector.sub(this.camera.position).normalize();

		const distance = - (this.camera.position.z) / dir.z;
		let pos = this.camera.position.clone().add(dir.multiplyScalar(distance));
		
		if (this.mouseMesh){
			this.mouseMesh.position.copy(pos);
		}

		if (this.hand){
			this.hand.position.copy(pos);
			this.hand.rotation.y = -0.2;
		}


		this.mouseX = ev.clientX - window.innerWidth / 2;
		this.mouseY = ev.clientY - (window.innerHeight * CONSTANTS.HEIGHT_RATIO) / 2;

		this.halfMouseX = (ev.clientX / 2) - window.innerWidth;
		this.targetHalfRotationX = this.halfMouseX * 0.001;

		this.targetRotationY = this.mouseY * 0.001;
		this.targetRotationX = this.mouseX * 0.001;


	}

	//--------------------------------------------
	// Resize
	//
			
	onResize(){
		this.windowHalfX = window.innerWidth / 2;
		this.windowHalfY = (window.innerHeight * CONSTANTS.HEIGHT_RATIO) / 2;

		this.w = window.innerWidth;
		this.h = window.innerHeight * CONSTANTS.HEIGHT_RATIO;

		this.camera.aspect = this.w / this.h;
		this.camera.updateProjectionMatrix();

		this.renderer.setSize(this.w, this.h);
	}

	//--------------------------------------------
	//
	// Render Loop
	//
	//--------------------------------------------
			
	render(){
		const self = this;
		const timer = 0.0001 * Date.now();



		//--------------------------------------------
		// Hand movements
		//
				
		if (self.hand){

			self.hand.rotation.x += self.targetRotationY * CONSTANTS.LATERAL_ARM_SPEED;
			self.hand.rotation.z -= self.targetRotationX * CONSTANTS.VERTICAL_ARM_SPEED;

			if (self.hand.rotation.x > 17.5){
				self.hand.rotation.x = 17.5;
			}

			if (self.hand.rotation.x < 16.9){
				self.hand.rotation.x = 16.9;
			}

			if (self.hand.rotation.z > 0){
				self.hand.rotation.z = 0;
			}

			if (self.hand.rotation.z < -1){
				self.hand.rotation.z = -1;
			}
		}



		this.renderer.render(this.scene, this.camera);
		
	}


	resume(){
		this.animate();
	}


	stop(){
		cancelAnimationFrame(this.animationLoop);
	}

	//--------------------------------------------
	// Animation Frame
	//		
	animate(){


		this.render();

		this.animationLoop = requestAnimationFrame(this.animate.bind(this));
	}

}


export default ArmScene;